/**
 * Visor de una solicitud de eliminación: a la izquierda el documento que se
 * conserva, a la derecha (en rojo) lo que se eliminará. En pantalla angosta
 * se apilan, primero lo que se elimina. Spec 2026-09-28-grupos-supervisoras §7.1.
 * Qué paneles y con qué enlace lo decide lib/preview_panes.js.
 */

import React, { useEffect, useState } from 'react';
import { useFirebase } from 'react-redux-firebase';
import { Drawer, Button, Space, Tag, Typography, Spin, Grid, Empty } from 'antd';

import { PdfViewerPane } from '../documents/pdf_viewer_pane';
import { previewPanes, formatXml } from '../../lib/preview_panes';

const XML_MAX_CHARS = 500000;

const XmlPane = ({ url }) => {
  const [state, setState] = useState({ loading: true, text: '', error: null });
  useEffect(() => {
    let alive = true;
    fetch(url)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then((t) => { if (alive) setState({ loading: false, text: formatXml(t.slice(0, XML_MAX_CHARS)), error: null }); })
      .catch(() => { if (alive) setState({ loading: false, text: '', error: 'No se pudo leer el XML.' }); });
    return () => { alive = false; };
  }, [url]);
  if (state.loading) return <Spin />;
  if (state.error) return <Typography.Text type="danger">{state.error}</Typography.Text>;
  return (
    <pre style={{ height: '70vh', overflow: 'auto', background: '#fafafa', padding: 12, fontSize: 12, margin: 0 }}>
      {state.text}
    </pre>
  );
};

const PaneBody = ({ pane }) => {
  if (pane.kind === 'missing') return <Empty description="Este archivo ya no existe" />;
  if (pane.kind === 'pdf') return <PdfViewerPane url={pane.url} />;
  if (pane.kind === 'xml') return <XmlPane url={pane.url} />;
  return (
    <Space direction="vertical">
      <Typography.Text>Este tipo de archivo no se puede previsualizar.</Typography.Text>
      <Button href={pane.url} target="_blank" rel="noopener noreferrer">Descargar {pane.name}</Button>
    </Space>
  );
};

const Pane = ({ pane }) => {
  const del = pane.role === 'delete';
  return (
    <div style={{
      flex: 1, minWidth: 0, padding: 12, borderRadius: 6,
      border: `2px solid ${del ? '#ff4d4f' : '#d9d9d9'}`,
      background: del ? '#fff1f0' : '#fff',
    }}>
      <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 8 }}>
        <Tag color={del ? 'red' : 'default'} style={{ whiteSpace: 'normal' }}>{pane.label}</Tag>
        {del && pane.reason ? <Typography.Text type="secondary">Motivo: {pane.reason}</Typography.Text> : null}
      </Space>
      <PaneBody pane={pane} />
    </div>
  );
};

export const PreviewDrawer = ({ request, open, onClose, onApprove, onReject, busy }) => {
  const firebase = useFirebase();
  const screens = Grid.useBreakpoint();
  const [docs, setDocs] = useState({ loading: false, parent: null, item: null });

  useEffect(() => {
    if (!open || !request) return undefined;
    let alive = true;
    setDocs({ loading: true, parent: null, item: null });
    const db = firebase.firestore();
    const read = (p) => (p ? db.doc(p).get().then((s) => (s.exists ? s.data() : null)).catch(() => null) : Promise.resolve(null));
    Promise.all([read(request.kind === 'document' ? null : request.parentPath), read(request.itemPath)])
      .then(([parent, item]) => { if (alive) setDocs({ loading: false, parent, item }); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, request?.id]);

  const panes = request ? previewPanes(request, docs.parent, docs.item) : [];
  const pending = request?.status === 'pending';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="100%"
      destroyOnHidden
      title={request ? `Solicitud de ${request.requestedByName || 'un usuario'}` : 'Solicitud'}
      extra={pending ? (
        <Space>
          <Button type="primary" danger loading={busy} onClick={() => onApprove(request)}>Aprobar</Button>
          <Button disabled={busy} onClick={() => onReject(request)}>Rechazar</Button>
        </Space>
      ) : null}
    >
      {docs.loading ? <Spin /> : (
        // [delete, keep]: en ancho, row-reverse deja el documento a la izquierda;
        // en angosto, column pone primero lo que se elimina.
        <div style={{ display: 'flex', gap: 16, flexDirection: screens.md ? 'row-reverse' : 'column' }}>
          {panes.map((p) => <Pane key={p.role} pane={p} />)}
        </div>
      )}
    </Drawer>
  );
};

export default PreviewDrawer;
